import { useTranslation } from '@rhino-project/core/i18n';
import {
  useTheme,
  Button,
  ToggleButtonGroup,
  ToggleButton
} from '@heroui/react';
import { Icon } from '@iconify/react';

const THEME_CHOICES = [
  { id: 'system', icon: 'bi:circle-half' },
  { id: 'light', icon: 'bi:sun' },
  { id: 'dark', icon: 'bi:moon-stars' }
] as const;

type ThemeVariant = 'full' | 'compact' | 'toggle';

interface ThemeSwitcherProps {
  // full (default): a labelled control with a "Theme" heading and three text
  //   segments — for settings panels and roomy menus.
  // compact: the three icons only, no heading or per-segment text — for tight
  //   toolbars where every choice should still be one tap away.
  // toggle: a single icon button showing the current theme that cycles
  //   System → Light → Dark on each press — for very tight spaces such as a
  //   site header.
  // className is applied to the control in the compact and toggle modes for
  // placement tweaks by the consumer.
  variant?: ThemeVariant;
  className?: string;
}

export const ThemeSwitcher = ({
  variant = 'full',
  className
}: ThemeSwitcherProps) => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme('system');

  if (variant === 'toggle') {
    const index = Math.max(
      0,
      THEME_CHOICES.findIndex((choice) => choice.id === theme)
    );
    const current = THEME_CHOICES[index];
    const next = THEME_CHOICES[(index + 1) % THEME_CHOICES.length];

    return (
      <Button
        isIconOnly
        variant="ghost"
        size="sm"
        className={className}
        aria-label={t('rhino.frontend.messages.theme_switch', {
          defaultValue: 'Theme: %{current}. Switch to %{next}',
          current: t(`rhino.frontend.messages.${current.id}`, {
            defaultValue:
              current.id.charAt(0).toUpperCase() + current.id.slice(1)
          }),
          next: t(`rhino.frontend.messages.${next.id}`, {
            defaultValue: next.id.charAt(0).toUpperCase() + next.id.slice(1)
          })
        })}
        onPress={() => setTheme(next.id)}
      >
        <Icon icon={current.icon} />
      </Button>
    );
  }

  const compact = variant === 'compact';

  const group = (
    <ToggleButtonGroup
      {...(compact
        ? {
            'aria-label': t('rhino.frontend.messages.theme', {
              defaultValue: 'Theme'
            }),
            className
          }
        : { 'aria-labelledby': 'theme-switcher-label' })}
      size="sm"
      fullWidth={!compact}
      selectionMode="single"
      selectedKeys={[theme]}
      onSelectionChange={(keys) => {
        const value = [...keys][0];
        if (value) setTheme(value as string);
      }}
    >
      {THEME_CHOICES.map(({ id, icon }) => (
        <ToggleButton
          key={id}
          id={id}
          aria-label={
            compact
              ? t(`rhino.frontend.messages.${id}`, {
                  defaultValue: id.charAt(0).toUpperCase() + id.slice(1)
                })
              : undefined
          }
        >
          <Icon icon={icon} />
          {!compact && (
            <span>
              {t(`rhino.frontend.messages.${id}`, {
                defaultValue: id.charAt(0).toUpperCase() + id.slice(1)
              })}
            </span>
          )}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  if (compact) return group;

  return (
    <div className="flex flex-col gap-1.5 px-2 py-1.5">
      <span id="theme-switcher-label" className="text-xs text-muted">
        {t('rhino.frontend.messages.theme')}
      </span>
      {group}
    </div>
  );
};
